import { useCallback, useState, Ref, useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import partition from "lodash/partition";
import useFetch from "use-http";

import Pagination from "~/components/shared/Pagination";
import usePagination, { PaginationProps } from "~/components/analytics/shared/hooks/usePagination";
import ErrorState from "~/components/analytics/shared/ErrorState";
import { Suite } from "~/components/analytics/shared/type";
import { track } from "~/lib/segmentAnalytics";
import ListItem, { NAVATAR_DIMENSIONS } from "~/components/analytics/suite/List/ListItem";
import EmptyState from "~/components/shared/EmptyState";

type Props = {
  endpoint: string;
  current_organization_member: boolean;
  query: string | null;
};

type SuiteProps = {
  suite: Suite;
  path: string;
};

const LoadingState = () => {
  return (
    <>
      {[...Array(8)].map((_el, index) => (
        <div key={index} className="rounded-3 gap4 p6 border-gray mb-2 flex items-center border">
          <div className="gap4 flex items-center">
            <Skeleton
              containerClassName="flex-1"
              width={NAVATAR_DIMENSIONS + "px"}
              height={NAVATAR_DIMENSIONS + "px"}
            />
            <Skeleton containerClassName="flex-1" width="200px" />
          </div>
          <Skeleton containerClassName="flex-1 text-right" width="180px" />
        </div>
      ))}
    </>
  );
};

const List = ({ endpoint, current_organization_member, query }: Props) => {
  const [refetchSuites, setRefetchSuites] = useState(false);
  const { currentData, pageCount, handleOnChange, loading, error } = usePagination({
    endpoint,
    refetch: refetchSuites,
  });

  const favoriteSuiteFetch = useFetch({
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": window._csrf.token,
    },
  });

  const handleBookmarkClick = useCallback(async (suite, buttonRef) => {
    buttonRef.current.disabled = true;

    _trackBookmarkButtonClicked({
      alreadyBookmarked: suite.favorite,
      suite_id: suite.id,
    });

    if (suite.favorite) {
      await favoriteSuiteFetch.del(suite.unfavorite_endpoint);
    } else {
      await favoriteSuiteFetch.post(suite.favorite_endpoint);
    }

    if (favoriteSuiteFetch.response.ok) {
      setRefetchSuites(true);
    } else {
      buttonRef.current.disabled = false;
    }
  }, []);

  useEffect(() => {
    setRefetchSuites(false);
  }, [currentData]);

  return (
    <ListData
      currentData={currentData}
      pageCount={pageCount}
      handleOnChange={handleOnChange}
      loading={loading}
      error={error}
      currentOrganizationMember={current_organization_member}
      onBookmarkClick={handleBookmarkClick}
      query={query}
    />
  );
};

type ListDataPaginationProps = Omit<PaginationProps, "currentData"> & {
  currentData: Array<SuiteProps>;
  currentOrganizationMember: boolean;
  onBookmarkClick: (suite: Suite, buttonRef: Ref<HTMLButtonElement>) => void;
  query: string | null;
};

export const ListData = ({
  currentData,
  loading,
  error,
  pageCount,
  handleOnChange,
  currentOrganizationMember,
  onBookmarkClick,
  query,
}: ListDataPaginationProps) => {
  if (error) {
    return (
      <ErrorState
        emoji="🕳️"
        heading="There was an error loading the test suites"
        subheading="Try refreshing the page; if the problem persists"
        urlText="let us know"
        url="mailto:support@buildkite.com"
      />
    );
  }

  if (!loading && !!query && currentData.length === 0) {
    return <EmptyState emoji="🕳️" heading={`No suites with the name or slug "${query}"`} />;
  }

  const [favoriteSuites, remainingSuites] = partition(currentData, (suite) => suite.suite.favorite);

  return (
    <div>
      {loading ? (
        <LoadingState />
      ) : (
        <>
          {favoriteSuites.length > 0 && currentOrganizationMember && (
            <section className="ta-suites-bookmark-wrapper">
              <h2 className="sr-only">Favorite suites</h2>
              <ul className="container">
                {favoriteSuites.map((suite) => (
                  <ListItem
                    key={suite.suite.id}
                    suite={suite.suite}
                    path={suite.path}
                    currentOrganizationMember={currentOrganizationMember}
                    onBookmarkClick={onBookmarkClick}
                  />
                ))}
              </ul>
            </section>
          )}

          <section>
            <ul>
              {remainingSuites.map((suite) => (
                <ListItem
                  key={suite.suite.id}
                  suite={suite.suite}
                  path={suite.path}
                  currentOrganizationMember={currentOrganizationMember}
                  onBookmarkClick={onBookmarkClick}
                />
              ))}
            </ul>
          </section>
        </>
      )}
      <Pagination pageCount={pageCount} onPageChange={handleOnChange} />
    </div>
  );
};

const _trackBookmarkButtonClicked = ({
  alreadyBookmarked,
  suite_id,
}: {
  alreadyBookmarked: boolean;
  suite_id: string;
}) => {
  track("TA Suite Bookmark button clicked", {
    source: "ListItem",
    action: alreadyBookmarked ? "suite_favorite#destroy" : "suite_favorite#create",
    bookmarked: alreadyBookmarked ? "Bookmark removed" : "Bookmark saved",
    suite_id: suite_id,
  });
};

export default List;
